/* eslint-disable no-useless-escape */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { connect, useDispatch } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as auth from '../_redux/authRedux';
import { Auth } from 'aws-amplify';
import parseUser from '../../../utils/parseUser';
import { TextField } from '../../../components/TextFieldInput';
import axios from 'axios';
import { InputBase, NativeSelect, Fade, Snackbar } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import { Alert } from '@material-ui/lab';
import { useDashboards } from '../../../contexts/DashboardContext';

/*
  INTL (i18n) docs:
  https://github.com/formatjs/react-intl/blob/master/docs/Components.md#formattedmessage
*/

/*
  Formik+YUP:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
*/
const LABEL_SIZE = 100;

const BootstrapInput = withStyles(theme => ({
  input: {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ced4da',
    fontSize: 14,
    fontFamily: ['Titillium', 'sans-serif'].join(','),
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}))(InputBase);

const initialValues = {
  email: '',
  password: '',
  newPassword: '',
  confirmNewPassword: '',
};

function Login(props) {
  const { intl } = props;
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [newUser, setNewUser] = useState(false);
  const [entities, setEntities] = useState([]);
  const [isUnauthorized, setUnauthorized] = useState(window.isUnauthorized);
  const [selectedEntity, setSelectedEntity] = useState({});
  const [error, setError] = useState('');
  const { getDashboards } = useDashboards();
  const dispatch = useDispatch();
  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email(
        intl.formatMessage({
          id: 'GENERAL.WRONG_EMAIL',
        })
      )
      .required(
        intl.formatMessage({
          id: 'AUTH.VALIDATION.REQUIRED_FIELD',
        })
      ),
    password: Yup.string().required(
      intl.formatMessage({
        id: 'AUTH.VALIDATION.REQUIRED_FIELD',
      })
    ),
    newPassword: Yup.string().matches(
      /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$*.\[\]{}\(\)?\-"!@#%&\/\\,><':;|_~`]).{8,}/,
      intl.formatMessage({
        id: 'AUTH.INPUT.REGEX',
      })
    ),
    confirmNewPassword: Yup.string(),
  });

  useEffect(() => {
    new Promise(resolve =>
      setTimeout(() => {
        window.isUnauthorized = false;
        setUnauthorized(false);
        resolve();
      }, 5000)
    );
  }, []);

  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  function onSelect(e) {
    e.preventDefault();
    e.stopPropagation();
    const clickedEntity = entities.find(x => x.id === parseInt(e.target.value));
    setSelectedEntity(clickedEntity);
  }

  async function confirmLogin() {
    const authenticated = await Auth.currentAuthenticatedUser();
    await Auth.updateUserAttributes(authenticated, {
      'custom:business_entity_id': `${selectedEntity.id}`,
      'custom:project_id': `${selectedEntity.projectId}`,
      'custom:impersonating_user': '',
    });
    const reassigned = await Auth.userSession(authenticated);
    if (import.meta.env.VITE_SKIP_DASHBOARD_LOAD !== 'true') {
      await getDashboards(reassigned.idToken.jwtToken);
    }
    dispatch(
      props.setUser(
        parseUser(user.payload, reassigned.idToken.jwtToken, selectedEntity)
      )
    );
  }

  async function handleNewUser() {
    setLoading(true);
    try {
      const user = await Auth.signIn(
        formik.values.email,
        formik.values.password
      );
      const login = await Auth.completeNewPassword(
        user,
        formik.values.newPassword
      );
      const { jwtToken, payload } = login.signInUserSession.idToken;
      setUser(payload);
      const { data } = await axios.get(`/users/${login.username}`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (data.length === 1) {
        try {
          await Auth.updateUserAttributes(login, {
            'custom:business_entity_id': `${data[0].id}`,
            'custom:project_id': `${data[0].projectId}`,
          });
        } catch (e) {
          console.error(e);
        }
        if (import.meta.env.VITE_SKIP_DASHBOARD_LOAD !== 'true') {
          await getDashboards(jwtToken);
        }
        dispatch(props.setUser(parseUser(payload, jwtToken, data[0])));
      } else {
        setUser({ payload, jwtToken });
        setEntities(data);
      }
    } finally {
      setLoading(false);
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: LoginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      enableLoading();
      setSubmitting(true);
      try {
        const login = await Auth.signIn(values.email, values.password);
        if (login.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setNewUser(true);
          return;
        }
        const { jwtToken, payload } = login.signInUserSession.idToken;
        setUser(payload);
        const { data } = await axios.get(`/users/${login.username}`, {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        });
        if (data.length === 1) {
          await Auth.updateUserAttributes(login, {
            'custom:business_entity_id': `${data[0].id}`,
            'custom:project_id': `${data[0].projectId}`,
            'custom:impersonating_user': '',
          });
          const reassigned = await Auth.userSession(login);
          if (import.meta.env.VITE_SKIP_DASHBOARD_LOAD !== 'true') {
            await getDashboards(reassigned.idToken.jwtToken);
          }
          dispatch(
            props.setUser(
              parseUser(payload, reassigned.idToken.jwtToken, data[0])
            )
          );
        } else {
          document.getElementById('email-input').disabled = true;
          document.getElementById('password-input').disabled = true;
          setSelectedEntity(data[0]);
          setUser({ payload, jwtToken });
          setEntities(data);
        }
      } catch (e) {
        console.error(e);
        setSubmitting(false);
        setStatus(
          intl.formatMessage({
            id: 'AUTH.VALIDATION.INVALID_LOGIN',
          })
        );
      } finally {
        disableLoading();
      }
    },
  });

  async function setupPassword(e) {
    e.preventDefault();
    if (!formik.values.newPassword || !formik.values.confirmNewPassword) {
      setError('AUTH.VALIDATION.REQUIRED_FIELD');
      return;
    }
    if (formik.values.newPassword !== formik.values.confirmNewPassword) {
      setError('AUTH.INPUT.MISMATCH');
      return;
    }
    if (
      !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$*.\[\]{}\(\)?\-"!@#%&\/\\,><':;|_~`]).{8,}/.test(
        formik.values.newPassword
      )
    ) {
      setError('AUTH.INPUT.REGEX');
      return;
    }
    await handleNewUser();
  }

  function cancelLogin() {
    document.getElementById('email-input').disabled = false;
    document.getElementById('password-input').disabled = false;
    setEntities([]);
    formik.resetForm();
  }

  return (
    <div
      className="login-form login-signin"
      id="kt_login_signin_form"
      style={{ maxWidth: 500 }}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isUnauthorized}
        autoHideDuration={1000}
      >
        <Alert
          severity="warning"
          className="align-items-center"
          elevation={6}
          variant="filled"
        >
          <h6>{intl.formatMessage({ id: `E401.TITLE` })}</h6>
          <span>{intl.formatMessage({ id: `E401.MESSAGE` })}</span>
        </Alert>
      </Snackbar>
      <form
        onSubmit={formik.handleSubmit}
        className="form fv-plugins-bootstrap fv-plugins-framework"
      >
        {newUser ? (
          <div>
            <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
              <div className="alert-text font-weight-bold text-center">
                {intl.formatMessage({ id: 'AUTH.INPUT.NEW_USER' })}
              </div>
            </div>
            <TextField
              field="newPassword"
              labelMinSize={100}
              formik={formik}
              type="password"
              label={intl.formatMessage({ id: 'AUTH.INPUT.NEW_PASSWORD' })}
            />
            <TextField
              field="confirmNewPassword"
              labelMinSize={100}
              formik={formik}
              type="password"
              label={intl.formatMessage({ id: 'AUTH.INPUT.CONFIRM_PASSWORD' })}
            />
            {error && (
              <div className="d-flex">
                <label style={{ minWidth: 100 }}> </label>
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {intl.formatMessage({ id: error })}
                  </div>
                </div>
              </div>
            )}
            <div className="form-group text-right">
              <button
                id="kt_login_signin_submit"
                type="submit"
                onClick={setupPassword}
                disabled={loading}
                className={`btn btn-primary font-weight-bold px-9 py-2 my-1`}
              >
                <span>{intl.formatMessage({ id: 'CONFIRM' })}</span>
                {loading && (
                  <span className="ml-3 spinner spinner-white"></span>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div>
            {formik.status ? (
              <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                <div className="alert-text font-weight-bold text-center">
                  {formik.status}
                </div>
              </div>
            ) : null}

            <TextField
              field="email"
              id="email-input"
              formik={formik}
              labelMinSize={LABEL_SIZE}
              label={intl.formatMessage({ id: 'AUTH.INPUT.EMAIL' })}
            />
            <TextField
              field="password"
              id="password-input"
              formik={formik}
              labelMinSize={LABEL_SIZE}
              type="password"
              label={intl.formatMessage({ id: 'AUTH.INPUT.PASSWORD' })}
            />
            {entities.length ? (
              <Fade in timeout={700}>
                <div
                  className={`form-group fv-plugins-icon-container d-flex align-items-center`}
                >
                  <label style={{ minWidth: LABEL_SIZE }}>
                    {intl.formatMessage({ id: 'LOGIN.ENTITIES.LABEL' })}
                  </label>
                  <div
                    className="login-form login-signin"
                    id="kt_login_signin_form"
                  >
                    <NativeSelect
                      onChange={onSelect}
                      defaultValue={selectedEntity.id}
                      className="form-select w-100"
                      style={{ minHeight: 41.5 }}
                      input={<BootstrapInput />}
                    >
                      {entities.map(opt => (
                        <option value={opt.id} key={opt.id}>
                          {' '}
                          {opt.name}{' '}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                </div>
              </Fade>
            ) : null}
            <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
              {!entities.length ? (
                <>
                  <div className="d-flex">
                    <div style={{ width: 100 }}></div>
                    <Link
                      to="/auth/forgot-password"
                      className="text-dark-50 text-hover-primary my-3 mr-2"
                      id="kt_login_forgot"
                    >
                      <FormattedMessage id="AUTH.GENERAL.FORGOT_BUTTON" />
                    </Link>
                  </div>
                  <button
                    id="kt_login_signin_submit"
                    type="submit"
                    disabled={formik.isSubmitting}
                    className={`btn btn-primary font-weight-bold px-6 py-2 my-1`}
                  >
                    {loading && (
                      <span className="mr-12 spinner spinner-white"></span>
                    )}
                    <span>Sign In</span>
                  </button>
                </>
              ) : (
                <div className="w-100 d-flex justify-content-end">
                  <button
                    type="button"
                    onClick={confirmLogin}
                    className={`btn btn-primary font-weight-bold px-9 py-2 my-1 mr-3`}
                  >
                    <span>Sign In</span>
                  </button>
                  <Fade in timeout={700}>
                    <button
                      type="button"
                      onClick={cancelLogin}
                      className={`cancel-btn btn btn-bordered font-weight-bold px-9 py-2 my-1`}
                    >
                      <span>{intl.formatMessage({ id: 'CANCEL' })}</span>
                    </button>
                  </Fade>
                </div>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

export default injectIntl(connect(null, auth.actions)(Login));
